import React from 'react'

import { isNumber } from 'lodash'
import Text from '~/views/components/ui/Text'

import { LoadingTextPlaceholder } from '~/views/components/ProjectOverview/components/ProjectOverviewCard'

import { useCurrentUserContext } from '~/views/contexts/CurrentUserContext'
import LocalizedCurrencyNumber from '~/views/components/currency/LocalizedCurrencyNumber'
import styled from 'styled-components'
import { getCurrencySymbol } from '~/utils/currency/currency'

import WarningIconAndTooltip from './../../../WarningIconAndTooltip'
import { useStaticLabels } from 'shared/hooks/use-static-labels'

/**
 * =====----===--=--===----=====
 * AccruedContent
 */

const AccruedContentContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`

type AccruedContentProps = {
  accruedCost?: number | null
  loading: boolean
}

export default function AccruedContent({
  accruedCost,
  loading,
}: AccruedContentProps) {
  const [getCommonLabel] = useStaticLabels(['common'])

  return (
    <AccruedContentContainer data-component-name={'accrued-cost-container'}>
      <Text data-component-name={'accrued-label'}>
        {getCommonLabel('accrued')}
      </Text>

      <AccruedContentValue accruedCost={accruedCost} loading={loading} />
    </AccruedContentContainer>
  )
}

/**
 * =====----===--=--===----=====
 * AccruedContentValue
 */

type AccruedContentValueProps = {
  accruedCost?: number | null
  loading: boolean
}

function AccruedContentValue({
  accruedCost,
  loading,
}: AccruedContentValueProps) {
  if (loading) {
    return <LoadingTextPlaceholder />
  }

  return !isNumber(accruedCost) ? (
    <DefaultAccruedContentValue />
  ) : (
    <AccruedContentNumberValue accruedCost={accruedCost} />
  )
}

/**
 * =====----===--=--===----=====
 * AccruedContentNumberValue
 */

type AccruedContentNumberValueProps = {
  accruedCost?: number | null
}

function AccruedContentNumberValue({
  accruedCost,
}: AccruedContentNumberValueProps) {
  const { currentUser } = useCurrentUserContext()

  return (
    <Text weight={'bold'} data-component-name={'accrued-value'}>
      <LocalizedCurrencyNumber
        number={accruedCost as number}
        currentUser={currentUser}
      />
    </Text>
  )
}

/**
 * =====----===--=--===----=====
 * DefaultAccruedContentValue
 */

const TooltipContentContainer = styled.div`
  padding: 0;

  .title {
    margin: 0;
  }

  .list-container {
    margin: 0;
    padding-top: 3px;
    color: #888;
    padding-left: 2em;

    .list-item {
      margin: 0;
      font-size: 0.85em;
      line-height: 135%;
    }
  }
`

function DefaultAccruedContentValue() {
  const [getProjectOverviewLabel] = useStaticLabels(['projectOverview'])
  const { currentUser } = useCurrentUserContext()

  return (
    <Text data-component-name={'accrued-value'}>
      <Text weight={'bold'}>
        {getCurrencySymbol(currentUser?.organization?.currencyCode)} -
      </Text>{' '}
      <WarningIconAndTooltip>
        <TooltipContentContainer>
          <Text className={'title'}>
            {getProjectOverviewLabel('projectOverviewAccruedUnavailableDueTo')}:
          </Text>

          <ul className={'list-container'}>
            <li className={'list-item'}>
              {getProjectOverviewLabel(
                'projectOverviewMissingFullLoadedCostsForUsers',
              )}
            </li>
          </ul>
        </TooltipContentContainer>
      </WarningIconAndTooltip>
    </Text>
  )
}
